import { createReducer } from 'redux-act';
import { isEmpty, reject } from 'ramda';
import storage from 'redux-persist/lib/storage';
import { persistReducer } from 'redux-persist';
import { PersistedState } from 'redux-persist/es/types';

import { IProduct } from '../../types/Product.types';
import { ISlot } from '../meeting/meeting.types';
import { IProspects } from '../requiredActions/requiredActions.types';
import { IVerifiedShippingValidation } from '../shipping/shipping.types';
import { TFullRecipient } from '../../../../store/users/users.types';
import { loadRequiredActionsSuccess } from '../requiredActions/requiredActions.actions';
import { fetchBookedMeetingInfo } from '../bookedMeetingInfo/bookedMeetingInfo.actions';
import { QUESTION_RA_TYPE, TERMS_RA_TYPE } from '../../constants/requiredActions.constants';

import {
  setActiveStatus,
  provideStatuses,
  setRecipient,
  setTimezone,
  setProspects,
  setShippingAddress,
  setProduct,
  setIsDonation,
  setCountryId,
  setConfirmedFlow,
  resetFlow,
  setCalendarPeriod,
  setMeetingSlots,
  setMeetingNotes,
  setHashId,
} from './giftFlow.actions';
import { getGiftFlowStatuses } from './giftFlow.helpers';
import { IFlowStatus } from './giftFlow.types';

export type TClearGiftFlowState = {
  hashId: string;
  activeStatus: number | null;
  statuses: IFlowStatus[];
  isDonation: boolean;
  recipient: TFullRecipient | null;
  product: IProduct | null;
  timezone: string;
  from: string;
  to: string;
  slots: ISlot[] | null;
  notes: string;
  prospects: IProspects | null;
  shipping: IVerifiedShippingValidation | null;
  countryId: number | null;
  isConfirmedFlow: boolean;
};

export type TGiftFlowState = TClearGiftFlowState & PersistedState;

export const initialState: TClearGiftFlowState = {
  hashId: '',
  activeStatus: null,
  statuses: [],
  isDonation: false,
  recipient: null,
  product: null,
  timezone: '',
  from: '',
  to: '',
  slots: null,
  notes: '',
  prospects: null,
  shipping: null,
  countryId: null,
  isConfirmedFlow: false,
};

const giftFlowPersistConfig = {
  key: 'AlyceLocal.GiftFlow',
  storage,
};

const giftFlowState = createReducer<TClearGiftFlowState>({}, initialState);

giftFlowState
  .on(setActiveStatus, (state, payload) => ({
    ...state,
    activeStatus: payload,
  }))
  .on(provideStatuses, (state, payload) => ({
    ...state,
    statuses: getGiftFlowStatuses(payload),
  }))
  .on(setRecipient, (state, payload) => ({
    ...state,
    recipient: payload,
  }))
  .on(setTimezone, (state, payload) => ({
    ...state,
    timezone: payload,
  }))
  .on(setProspects, (state, payload) => ({
    ...state,
    prospects: reject(isEmpty, payload),
  }))
  .on(setShippingAddress, (state, payload) => ({
    ...state,
    shipping: payload,
  }))
  .on(setProduct, (state, payload) => ({
    ...state,
    product: payload,
  }))
  .on(setIsDonation, (state, payload) => ({
    ...state,
    isDonation: payload,
  }))
  .on(setCountryId, (state, payload) => ({
    ...state,
    countryId: payload,
  }))
  .on(setConfirmedFlow, (state, payload) => ({
    ...state,
    isConfirmedFlow: payload,
  }))
  .on(setCalendarPeriod, (state, payload) => ({
    ...state,
    to: payload.to,
    from: payload.from,
  }))
  .on(setMeetingSlots, (state, payload) => ({
    ...state,
    slots: payload,
  }))
  .on(setMeetingNotes, (state, payload) => ({
    ...state,
    notes: payload,
  }))
  .on(setHashId, (state, payload) => ({
    ...state,
    hashId: payload,
  }))
  .on(resetFlow, () => ({
    ...initialState,
  }));

giftFlowState.on(loadRequiredActionsSuccess, (state, payload) => {
  const prospects = payload.reduce((acc, item) => {
    if (item.type === QUESTION_RA_TYPE) {
      return { ...acc, answer: item.recipientAnswer };
    }
    if (item.type === TERMS_RA_TYPE) {
      return { ...acc, policy: !!item.recipientAnswer };
    }
    return { ...acc, [item.type]: item.recipientAnswer };
  }, {});
  return {
    ...state,
    prospects,
  };
});

giftFlowState.on(fetchBookedMeetingInfo.fulfilled, (state, { data, hasIntegration }) => {
  const { slots = null, timezone, notes } = data || {};
  return {
    ...state,
    timezone: timezone ?? state.timezone,
    notes: notes ?? state.notes,
    slots: hasIntegration && slots && slots.length > 1 ? slots.slice(0, 1) : slots,
  };
});

export const giftFlow = persistReducer(giftFlowPersistConfig, giftFlowState);
